import { allElements } from "constants/constants";
import { LocationsEntity } from "entities/Locations/LocationsEntity";
import { useCallback, useContext, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { IDetailsReportFilters, IOption } from "./DetailsFiltersInterfaces";
import { MillDetailsReportContext } from "contexts/MillDetailsReportContext/MillDetailsReportContext";
import { MillDashboardContext } from "contexts/MillDashboardsContext/MillDashboardsContext";
import { handleNameTranslation } from "utilities/localesUtilities";
import { objectComparator } from "utilities/utilities";

export const initialDetailsReportFilters: IDetailsReportFilters = {
    productionLine: [allElements],
    machineCenter: [allElements],
    functionalLocation: allElements,
    shift: allElements,
    operator: allElements,
    failureTypes: [allElements],
    failureCodes: [allElements],
};

export const validMillDetailsReportDefaultFields = [
    "productionLine",
    "machineCenter",
    "functionalLocation",
    "shift",
    "failureTypes",
    "operator",
    "failureCodes",
];

const useDetailsReportFilters = () => {
    const {
        handleFiltersChange: handleSetContextFilters,
        filters: filtersContext,
        handleRemoveDefaultFilters,
        handleSetFiltersDefault,
        loadingDefaultOperations,
        userFiltersDefault,
    } = useContext(MillDetailsReportContext);
    const { reportRangeEndDate: endDate, reportRangeStartDate: startDate } = useContext(MillDashboardContext);

    const { millId } = useParams();
    const [filters, setFilters] = useState<IDetailsReportFilters>(filtersContext);
    const { detailsFiltersData, loading } = LocationsEntity.GetDetailsFilters(Number(millId), {
        ...filters,
        startDate,
        endDate,
    });
    const isLoading = loading || !detailsFiltersData.production_line;
    const { t, i18n } = useTranslation();
    const allLabel = useMemo(() => t("locations.summary_report.filters.all"), [t]);

    const productionLinesOptions = useMemo(() => {
        if (loading) return [{ id: allElements, name: allLabel }];
        const productionLinesList =
            detailsFiltersData?.production_line?.map((productionLine) => {
                return {
                    id: productionLine.production_line_id,
                    name: productionLine.name,
                } as IOption;
            }) || [];

        productionLinesList?.unshift({ id: allElements, name: allLabel });
        return productionLinesList;
    }, [allLabel, loading, detailsFiltersData?.production_line]);

    const functionalLocationsOptions = useMemo(() => {
        if (loading) return [{ id: allElements, name: allLabel }];
        const functionalLocationsList =
            detailsFiltersData?.functional_location
                ?.filter((functionalLocation) =>
                    filters.productionLine?.length! > 0 &&
                    !filters.productionLine?.includes(allElements) &&
                    Array.isArray(filters.productionLine)
                        ? filters.productionLine?.find(
                              (el) => el === functionalLocation?.area?.machine_center?.production_line_id,
                          )
                        : true,
                )
                .filter((functionalLocation) =>
                    filters.machineCenter?.length! > 0 &&
                    !filters.machineCenter?.includes(allElements) &&
                    Array.isArray(filters.machineCenter)
                        ? filters.machineCenter?.find(
                              (el) => el === functionalLocation?.area?.machine_center?.machine_center_id,
                          )
                        : true,
                )
                .map((functionalLocation) => {
                    return {
                        id: functionalLocation.functional_location_id,
                        name: handleNameTranslation(i18n.language, functionalLocation.translation.translation_names),
                    } as IOption;
                }) || [];

        functionalLocationsList.unshift({ id: allElements, name: allLabel });
        return functionalLocationsList;
    }, [
        allLabel,
        loading,
        detailsFiltersData?.functional_location,
        filters.productionLine,
        filters.machineCenter,
        i18n.language,
    ]);

    const machineCentersOptions = useMemo(() => {
        if (loading) return [{ id: allElements, name: allLabel }];
        const machineCentersList =
            detailsFiltersData?.machine_center
                ?.filter((machineCenter) =>
                    filters.productionLine?.length! > 0 &&
                    !filters.productionLine?.includes(allElements) &&
                    Array.isArray(filters.productionLine)
                        ? filters.productionLine?.find((el) => el === machineCenter.production_line_id)
                        : true,
                )
                .map((machineCenter) => {
                    return {
                        id: machineCenter.machine_center_id,
                        name: machineCenter.name,
                    } as IOption;
                }) || [];

        machineCentersList.unshift({ id: allElements, name: allLabel });
        return machineCentersList;
    }, [allLabel, loading, detailsFiltersData?.machine_center, filters.productionLine]);

    const shiftsOptions = useMemo(() => {
        if (loading) return [{ id: allElements, name: allLabel }];
        const shiftsList =
            detailsFiltersData?.shift
                ?.filter((shift) =>
                    filters.productionLine?.length! > 0 &&
                    !filters.productionLine?.includes(allElements) &&
                    Array.isArray(filters.productionLine)
                        ? filters.productionLine?.find((el) =>
                              shift.machine_centers.find(
                                  (machineCenter) => machineCenter?.machine_center?.production_line_id === el,
                              ),
                          )
                        : true,
                )
                .filter((shift) =>
                    filters.machineCenter?.length! > 0 &&
                    !filters.machineCenter?.includes(allElements) &&
                    Array.isArray(filters.machineCenter)
                        ? filters.machineCenter?.find((el) =>
                              shift.machine_centers.find(
                                  (machineCenter) => machineCenter?.machine_center?.machine_center_id === el,
                              ),
                          )
                        : true,
                )
                .filter((shift) =>
                    filters.functionalLocation !== allElements
                        ? shift.functional_locations.find(
                              (el) => el.functional_location_id === filters.functionalLocation,
                          )
                        : true,
                )
                .map((shift) => {
                    return {
                        id: shift.shift_id,
                        name: shift.name,
                    } as IOption;
                }) || [];

        shiftsList.unshift({ id: allElements, name: allLabel });
        return shiftsList;
    }, [
        allLabel,
        loading,
        detailsFiltersData?.shift,
        filters.productionLine,
        filters.machineCenter,
        filters.functionalLocation,
    ]);

    const operatorsOptions = useMemo(() => {
        if (loading) return [{ id: allElements, name: allLabel }];
        const operatorsList =
            detailsFiltersData?.user
                ?.filter((user) =>
                    filters.productionLine?.length! > 0 &&
                    !filters.productionLine?.includes(allElements) &&
                    Array.isArray(filters.productionLine)
                        ? filters.productionLine?.find((el) =>
                              user.machine_centers.find(
                                  (machineCenter) => machineCenter?.machine_center?.production_line_id === el,
                              ),
                          )
                        : true,
                )
                .filter((user) =>
                    filters.machineCenter?.length! > 0 &&
                    !filters.machineCenter?.includes(allElements) &&
                    Array.isArray(filters.machineCenter)
                        ? filters.machineCenter?.find((el) =>
                              user.machine_centers.find(
                                  (machineCenter) => machineCenter?.machine_center?.machine_center_id === el,
                              ),
                          )
                        : true,
                )
                .filter((user) =>
                    filters.functionalLocation !== allElements
                        ? user.functional_locations.find(
                              (el) => el.functional_location_id === filters.functionalLocation,
                          )
                        : true,
                )
                .filter((user) =>
                    filters.shift !== allElements
                        ? user.shifts.find((el) => el.shift_day.shift_id === filters.shift)
                        : true,
                )
                .map((operator) => {
                    return {
                        id: operator.user_id,
                        name: `${operator.first_name} ${operator.last_name}`,
                    } as IOption;
                }) || [];

        operatorsList.unshift({ id: allElements, name: allLabel });
        return operatorsList;
    }, [
        allLabel,
        loading,
        detailsFiltersData?.user,
        filters.productionLine,
        filters.machineCenter,
        filters.functionalLocation,
        filters.shift,
    ]);

    const failureTypesOptions = useMemo(() => {
        if (loading) return [{ id: allElements, name: allLabel }];
        const failureTypesList =
            detailsFiltersData?.failure_type
                ?.filter((failure_type) =>
                    filters.productionLine?.length! > 0 &&
                    !filters.productionLine?.includes(allElements) &&
                    Array.isArray(filters.productionLine)
                        ? filters.productionLine?.find((el) =>
                              failure_type.machine_centers.find(
                                  (machineCenter) => machineCenter?.machine_center?.production_line_id === el,
                              ),
                          )
                        : true,
                )
                .filter((failure_type) =>
                    filters.machineCenter?.length! > 0 &&
                    !filters.machineCenter?.includes(allElements) &&
                    Array.isArray(filters.machineCenter)
                        ? filters.machineCenter?.find((el) =>
                              failure_type.machine_centers.find(
                                  (machineCenter) => machineCenter?.machine_center?.machine_center_id === el,
                              ),
                          )
                        : true,
                )
                .filter((failure_type) =>
                    filters.functionalLocation !== allElements
                        ? failure_type.functional_locations.find(
                              (el) => el.functional_location_id === filters.functionalLocation,
                          )
                        : true,
                )
                .filter((failure_type) =>
                    filters.shift !== allElements
                        ? failure_type.shifts.find((el) => el?.shift_day?.shift_id === filters.shift)
                        : true,
                )
                .filter((failure_type) =>
                    filters.operator !== allElements
                        ? failure_type.operators.find((el) => el.user_id === filters.operator)
                        : true,
                )
                .map((failureType) => {
                    return {
                        id: failureType.failure_type_id,
                        name: failureType.name,
                    } as IOption;
                }) || [];

        failureTypesList?.unshift({ id: allElements, name: allLabel });
        return failureTypesList;
    }, [
        allLabel,
        loading,
        detailsFiltersData?.failure_type,
        filters.productionLine,
        filters.machineCenter,
        filters.functionalLocation,
        filters.shift,
        filters.operator,
    ]);

    const failureCodesOptions: IOption[] = useMemo(() => {
        if (loading) return [{ id: allElements, name: allLabel }];

        const failureCodesList: IOption[] =
            detailsFiltersData?.failure_code
                ?.filter((failure_type) =>
                    filters.productionLine?.length! > 0 &&
                    !filters.productionLine?.includes(allElements) &&
                    Array.isArray(filters.productionLine)
                        ? filters.productionLine?.find((el) =>
                              failure_type.machine_centers.find(
                                  (machineCenter) => machineCenter?.machine_center?.production_line_id === el,
                              ),
                          )
                        : true,
                )
                .filter((failure_type) =>
                    filters.machineCenter?.length! > 0 &&
                    !filters.machineCenter?.includes(allElements) &&
                    Array.isArray(filters.machineCenter)
                        ? filters.machineCenter?.find((el) =>
                              failure_type.machine_centers.find(
                                  (machineCenter) => machineCenter?.machine_center?.machine_center_id === el,
                              ),
                          )
                        : true,
                )
                .filter((failure_type) =>
                    filters.functionalLocation !== allElements
                        ? failure_type.functional_locations.find(
                              (el) => el.functional_location_id === filters.functionalLocation,
                          )
                        : true,
                )
                .filter((failure_type) =>
                    filters.shift !== allElements
                        ? failure_type.shifts.find((el) => el?.shift_day?.shift_id === filters.shift)
                        : true,
                )
                .filter((failure_type) =>
                    filters.operator !== allElements
                        ? failure_type.operators.find((el) => el.user_id === filters.operator)
                        : true,
                )
                .filter((failureCode) =>
                    filters.failureTypes?.includes(allElements)
                        ? true
                        : Array.isArray(filters.failureTypes) &&
                          failureCode?.failure_type_id &&
                          filters.failureTypes.includes(failureCode.failure_type_id),
                )
                .map((failureCode) => {
                    return {
                        id: failureCode.failure_code_id,
                        name: failureCode.name,
                    } as IOption;
                }) || [];

        failureCodesList?.unshift({ id: allElements, name: allLabel });
        return failureCodesList;
    }, [
        allLabel,
        loading,
        detailsFiltersData?.failure_code,
        filters.failureTypes,
        filters.productionLine,
        filters.machineCenter,
        filters.functionalLocation,
        filters.shift,
        filters.operator,
    ]);

    const handleFiltersChange = useCallback(
        (newFilters: IDetailsReportFilters) => {
            if (Array.isArray(newFilters.productionLine)) {
                if (newFilters.productionLine.length <= 1) {
                    newFilters.productionLine = [allElements];
                } else if (
                    newFilters.productionLine.length > 0 &&
                    newFilters.productionLine.indexOf(allElements) === 0
                ) {
                    newFilters.productionLine = newFilters.productionLine.filter((productionLine) => {
                        return productionLine !== allElements;
                    });
                } else if (newFilters.productionLine.length > 1 && newFilters.productionLine.includes(allElements)) {
                    newFilters.productionLine = newFilters.productionLine.filter((productionLine) => {
                        return productionLine === allElements;
                    });
                }
            }

            if (Array.isArray(newFilters.machineCenter)) {
                if (newFilters.machineCenter.length <= 1) {
                    newFilters.machineCenter = [allElements];
                } else if (newFilters.machineCenter.length > 0 && newFilters.machineCenter.indexOf(allElements) === 0) {
                    newFilters.machineCenter = newFilters.machineCenter.filter((machineCenter) => {
                        return machineCenter !== allElements;
                    });
                } else if (newFilters.machineCenter.length > 1 && newFilters.machineCenter.includes(allElements)) {
                    newFilters.machineCenter = newFilters.machineCenter.filter((machineCenter) => {
                        return machineCenter === allElements;
                    });
                }
            }

            if (Array.isArray(newFilters.failureTypes)) {
                if (newFilters.failureTypes.length <= 1) {
                    newFilters.failureTypes = [allElements];
                } else if (newFilters.failureTypes.length > 0 && newFilters.failureTypes.indexOf(allElements) === 0) {
                    newFilters.failureTypes = newFilters.failureTypes.filter((failureType) => {
                        return failureType !== allElements;
                    });
                } else if (newFilters.failureTypes.length > 1 && newFilters.failureTypes.includes(allElements)) {
                    newFilters.failureTypes = newFilters.failureTypes.filter((failureType) => {
                        return failureType === allElements;
                    });
                }
            }

            if (Array.isArray(newFilters.failureCodes)) {
                if (newFilters.failureCodes.length <= 1) {
                    newFilters.failureCodes = [allElements];
                } else if (newFilters.failureCodes.length > 0 && newFilters.failureCodes.indexOf(allElements) === 0) {
                    newFilters.failureCodes = newFilters.failureCodes.filter((failureCode) => {
                        return failureCode !== allElements;
                    });
                } else if (newFilters.failureCodes.length > 1 && newFilters.failureCodes.includes(allElements)) {
                    newFilters.failureCodes = newFilters.failureCodes.filter((failureCode) => {
                        return failureCode === allElements;
                    });
                }
            }

            setFilters({ ...filters, ...newFilters });
        },
        [filters],
    );

    const applyFilters = () => {
        handleSetContextFilters({ ...filters, startDate, endDate });
    };

    const resetFilters = () => {
        setFilters({
            ...initialDetailsReportFilters,
            startDate,
            endDate,
        });
        handleSetContextFilters({
            ...initialDetailsReportFilters,
            startDate,
            endDate,
        });
    };

    const setFiltersDefault = async () => {
        try {
            await handleSetFiltersDefault(filters);
            handleSetContextFilters({ ...filters, startDate, endDate });
        } catch (error) {
            console.error(error);
        }
    };

    const removeDefaultFilters = async () => {
        try {
            await handleRemoveDefaultFilters();
            resetFilters();
        } catch (error) {
            console.error(error);
        }
    };

    const isFiltersDefault = useMemo(() => {
        if (userFiltersDefault) {
            return objectComparator(userFiltersDefault, filters, validMillDetailsReportDefaultFields);
        }

        return false;
    }, [userFiltersDefault, filters]);

    return {
        isLoading,
        productionLinesOptions,
        machineCentersOptions,
        functionalLocationsOptions,
        failureTypesOptions,
        failureCodesOptions,
        shiftsOptions,
        operatorsOptions,
        applyFilters,
        resetFilters,
        filters,
        handleFiltersChange,
        setFiltersDefault,
        removeDefaultFilters,
        isFiltersDefault,
        userFiltersDefault,
        loadingDefaultOperations,
    };
};

export default useDetailsReportFilters;
